import { useMemo, useState } from 'react';
import { PageSection } from '@framework/ui/elements/PageSection';
import type { GroupEntity } from '@group/domain';
import { Loading } from '@framework/ui/atoms/Loading';
import { useAssignedWorkspaces, WorkspaceList } from '@workspace/ui/WorkspaceList';
import { Input } from '@framework/ui/atoms/Input';
import { useWorkspaceFilter } from './utils';
import { CreateWorkspaceButton } from './createWorkspace';
import type { UserId } from 'balus-common';
import { useGroupMyMemberRole } from '@group/hooks/useGroupMyMemberRole';
import { useInternalPublicWorkspaces } from '@group/hooks/useInternalPublicWorkspaces';
import { useTranslation } from 'react-i18next';

type Props = {
  currentUserId: UserId;
  group: GroupEntity;
};

export const GroupContent: React.FC<Props> = ({ currentUserId, group }: Props) => {
  const { t } = useTranslation();
  const workspaces = useAssignedWorkspaces(currentUserId, group.id);
  const internalPublicWorkspaces = useInternalPublicWorkspaces(group.id);
  const isAdminRole = useGroupMyMemberRole(group.id) === 'admin';
  const [query, setQuery] = useState<string>('');

  const filteredWorkspaces = useWorkspaceFilter(
    useMemo(() => {
      if (workspaces === null) return null;
      // グループ画面のワークスペース一覧には、パーソナルワークスペースは表示しない
      return workspaces.filter((ws) => ws.isShared);
    }, [workspaces]),
    query
  );

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
  };

  return (
    <PageSection>
      <div className="flex items-center justify-between pb-5">
        <div className="truncate text-2xl">{t('group.workspaceList.title')}</div>

        <div className="flex flex-row items-center justify-end">
          {/* グループメンバーのロールが "Admin" の場合にのみ、グループ編集、ワークスペース作成が可能。 */}
          {isAdminRole && <CreateWorkspaceButton groupId={group.id} userId={currentUserId} />}
          <div className="w-2/3 pl-4">
            <Input
              type="text"
              placeholder={t('group.workspaceList.searchPlaceholder')}
              value={query}
              onChange={handleOnChange}
            />
          </div>
        </div>
      </div>
      {filteredWorkspaces === null || internalPublicWorkspaces === undefined ? (
        <Loading />
      ) : filteredWorkspaces.length === 0 ? (
        <div className={'items-center p-20 text-center text-lg'}>
          {t('group.workspaceList.empty')}
          <br />
          <br />
          {t('group.workspaceList.emptyDescription')}
        </div>
      ) : (
        <WorkspaceList workspaces={filteredWorkspaces} internalPublicWorkspaces={internalPublicWorkspaces} />
      )}
    </PageSection>
  );
};
